import { useLayoutEffect, useRef, useState } from 'react';
import ExportPageFooter from './ExportPageFooter';
import { useFluidLayout } from '../layout';
import {
  FOOTER_INSET_PX,
  PAGE_HEIGHT_PX,
  PAGE_MARGIN_PX,
  PAGE_WIDTH_PX,
} from './ExportPage';

const FOOTER_CLEARANCE_PX = 45;
const PAGE_TOP_PX = 24;
const PAGE_CHASSIS_PX = PAGE_TOP_PX + PAGE_MARGIN_PX + 8;

function DefaultHeader({ i, pages, sectionLabel, title, badge }) {
  return (
    <div className="flex items-start justify-between">
      <div>
        {sectionLabel && (
          <div className="text-[var(--td-primary)] text-2xs font-semibold tracking-widest uppercase mb-1">
            {sectionLabel}
          </div>
        )}
        <div
          className="text-3xl"
          style={{ fontFamily: 'var(--td-font-display)' }}
        >
          {title}
        </div>
      </div>

      {(badge || pages > 1) && (
        <div className="export-stamp flex-shrink-0">
          {badge || `${i + 1} / ${pages}`}
        </div>
      )}
    </div>
  );
}

export default function ExportFlowPage({
  sectionLabel,
  title,
  badge,
  header,
  aside,
  footerStatus = false,
  trip,
  tourOperator,
  settings,
  children,
}) {
  const fluid = useFluidLayout();
  const contentRef = useRef(null);
  const innerRef = useRef(null);
  const headerRef = useRef(null);
  const [pages, setPages] = useState(1);
  const [pageOffsets, setPageOffsets] = useState([0]);
  const [pageSlacks, setPageSlacks] = useState([0]);

  useLayoutEffect(() => {
    const outer = contentRef.current;
    const inner = innerRef.current;
    const header = headerRef.current;
    if (fluid || !outer || !inner) return;

    let rafId = null;
    const lastMeasure = { pages: null, pageOffsets: null, slacks: null };

    const measure = () => {
      const headerH = header?.getBoundingClientRect().height || 60;
      const fh = PAGE_HEIGHT_PX - PAGE_CHASSIS_PX - headerH;
      if (fh <= 0) return;

      const rawAvailable = fh - FOOTER_CLEARANCE_PX;
      const computed = window.getComputedStyle(inner);

      let lineHeight = parseFloat(computed.lineHeight || '0');
      if (isNaN(lineHeight) || lineHeight <= 0) {
        const fontSize = parseFloat(computed.fontSize || '16');
        lineHeight = fontSize * 1.2;
      }

      const availableP1 = rawAvailable;
      const availableNext = availableP1;

      const total = inner.getBoundingClientRect().height + 2;
      if (availableP1 <= 0 || availableNext <= 0) return;

      const selectors = [
        'img',
        'table',
        'li',
        '[data-prevent-cut]',
        '.font-semibold',
      ];
      const unbreakables = Array.from(
        inner.querySelectorAll(selectors.join(',')),
      ).filter((el) => {
        const protectedAncestor =
          el.parentElement?.closest('[data-prevent-cut]');
        return !protectedAncestor || !inner.contains(protectedAncestor);
      });

      const unbreakableRects = unbreakables
        .map((el) => {
          const rect = el.getBoundingClientRect();
          const top = rect.top - inner.getBoundingClientRect().top;
          return { el, top, bottom: top + rect.height, height: rect.height };
        })
        .filter(
          (r) => r.height > lineHeight && r.height < availableNext * 0.95,
        );

      const innerTop = inner.getBoundingClientRect().top;
      const lineRects = [];
      const walker = document.createTreeWalker(inner, NodeFilter.SHOW_TEXT);
      const range = document.createRange();
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (!node.nodeValue?.trim()) continue;
        range.selectNodeContents(node);
        for (const rect of range.getClientRects()) {
          if (rect.height > 0) {
            lineRects.push({
              top: rect.top - innerTop,
              bottom: rect.bottom - innerTop,
            });
          }
        }
      }

      const snapToLine = (coord, floorCoord) => {
        let snapped = coord;
        for (let guard = 0; guard < 50; guard += 1) {
          let earliestTop = null;
          for (const rect of lineRects) {
            if (rect.top < snapped - 0.5 && rect.bottom > snapped + 0.5) {
              if (earliestTop === null || rect.top < earliestTop) {
                earliestTop = rect.top;
              }
            }
          }
          if (earliestTop === null) return snapped;
          if (earliestTop <= floorCoord) return coord;
          snapped = earliestTop;
        }
        return snapped;
      };

      const offsets = [0];
      const slacks = [];
      let currentChildCoord = 0;
      let isFirstPage = true;

      while (offsets.length <= 50) {
        if (!isFirstPage && currentChildCoord >= total - 1) break;

        const pageCapacity = isFirstPage ? availableP1 : availableNext;
        const pageStartCoord = currentChildCoord;

        if (pageStartCoord + pageCapacity >= total - 1) {
          slacks.push(Math.max(0, rawAvailable - (total - pageStartCoord)));
          break;
        }

        let nextLimitCoord = pageStartCoord + pageCapacity;
        let earliestCross = null;
        for (const r of unbreakableRects) {
          if (r.top < nextLimitCoord && r.bottom > nextLimitCoord) {
            if (earliestCross === null || r.top < earliestCross.top) {
              earliestCross = r;
            }
          }
        }

        if (earliestCross && earliestCross.top > pageStartCoord + 10) {
          nextLimitCoord = earliestCross.top;
        } else {
          nextLimitCoord = snapToLine(nextLimitCoord, pageStartCoord + 10);
        }

        offsets.push(nextLimitCoord);
        slacks.push(
          Math.max(0, rawAvailable - (nextLimitCoord - pageStartCoord)),
        );
        currentChildCoord = nextLimitCoord;
        isFirstPage = false;
      }

      while (offsets.length > 1 && offsets[offsets.length - 1] >= total) {
        offsets.pop();
        slacks.pop();
      }

      const needed = Math.max(1, offsets.length);
      const offsetsChanged =
        !lastMeasure.pageOffsets ||
        lastMeasure.pageOffsets.length !== offsets.length ||
        offsets.some((v, idx) => v !== lastMeasure.pageOffsets[idx]);
      const slacksChanged =
        !lastMeasure.slacks ||
        lastMeasure.slacks.length !== slacks.length ||
        slacks.some((v, idx) => v !== lastMeasure.slacks[idx]);

      if (offsetsChanged) setPageOffsets(offsets);
      if (lastMeasure.pages !== needed) setPages(needed);
      if (slacksChanged) setPageSlacks(slacks);

      lastMeasure.pageOffsets = offsets;
      lastMeasure.pages = needed;
      lastMeasure.slacks = slacks;
    };

    const schedule = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    let cancelled = false;
    schedule();
    const observer = new ResizeObserver(schedule);
    observer.observe(outer);
    observer.observe(inner);
    document.fonts.ready.then(() => {
      if (!cancelled) schedule();
    });
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [fluid]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderHeader = (i) => (
    <div className="flex-shrink-0" ref={i === 0 ? headerRef : null}>
      {header ? (
        header(i)
      ) : (
        <DefaultHeader
          i={i}
          pages={pages}
          sectionLabel={sectionLabel}
          title={title}
          badge={badge}
        />
      )}
    </div>
  );

  if (fluid) {
    return (
      <div
        className="export-page relative mx-auto flex w-full flex-col bg-[var(--td-paper)]"
        style={{
          maxWidth: `${PAGE_WIDTH_PX}px`,
          padding: `${PAGE_TOP_PX}px ${PAGE_MARGIN_PX}px ${PAGE_MARGIN_PX}px`,
          boxSizing: 'border-box',
        }}
      >
        {renderHeader(0)}
        {aside}
        <div className="mt-3">{children}</div>
        <div className="mt-6">
          <ExportPageFooter
            trip={trip}
            tourOperator={tourOperator}
            settings={settings}
            status={footerStatus}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      {Array.from({ length: pages }, (_, i) => (
        <div
          key={i}
          className="export-page relative mx-auto bg-[var(--td-paper)] overflow-hidden flex flex-col"
          style={{
            width: `${PAGE_WIDTH_PX}px`,
            height: `${PAGE_HEIGHT_PX}px`,
            padding: `${PAGE_TOP_PX}px ${PAGE_MARGIN_PX}px ${PAGE_MARGIN_PX}px`,
            boxSizing: 'border-box',
          }}
        >
          {renderHeader(i)}

          <div
            className="flex-1 min-h-0 relative overflow-hidden mt-3"
            ref={i === 0 ? contentRef : null}
          >
            {aside && <div className="absolute inset-x-0 top-0">{aside}</div>}

            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom:
                  FOOTER_CLEARANCE_PX +
                  (i === pages - 1 ? 0 : (pageSlacks[i] ?? 0)),
                left: 0,
                right: 0,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: i === 0 ? 0 : -(pageOffsets[i] ?? 0),
                  left: 0,
                  right: 0,
                }}
                ref={i === 0 ? innerRef : null}
              >
                {children}
              </div>
            </div>
          </div>

          <div
            className="absolute"
            style={{
              left: `${PAGE_MARGIN_PX}px`,
              right: `${PAGE_MARGIN_PX}px`,
              bottom: `${FOOTER_INSET_PX}px`,
            }}
          >
            <ExportPageFooter
              trip={trip}
              tourOperator={tourOperator}
              settings={settings}
              status={footerStatus}
            />
          </div>
        </div>
      ))}
    </>
  );
}
